import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel,
  Slider,
  Paper,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Grid
} from '@mui/material';
import { styled } from '@mui/system';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import BorderStyleIcon from '@mui/icons-material/BorderStyle';
import BorderAllIcon from '@mui/icons-material/BorderAll';
import BorderOuterIcon from '@mui/icons-material/BorderOuter';
import BorderClearIcon from '@mui/icons-material/BorderClear';
import PaletteIcon from '@mui/icons-material/Palette';
import CloseIcon from '@mui/icons-material/Close';

const OptionsPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 45, 0.95)' : 'rgba(255, 255, 255, 0.95)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  backdropFilter: 'blur(10px)',
  width: '100%',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
  animation: 'fadeIn 0.3s ease-out',
  maxHeight: '90vh',
  overflowY: 'auto',
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(10px)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)'
    }
  }
}));

const PreviewBox = styled(Box)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)',
  minHeight: 150,
  overflow: 'hidden',
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
}));

const ColorButton = styled(Box)(({ theme, color }) => ({
  width: 36,
  height: 36,
  borderRadius: '50%',
  backgroundColor: color,
  border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.1)',
    boxShadow: '0 0 0 4px rgba(25, 118, 210, 0.2)'
  }
}));

const OptionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '& svg': {
    fontSize: '1.2rem'
  }
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  padding: 4,
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)',
  },
  zIndex: 10 // Ensure button is above other elements
}));

const ColorGrid = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1),
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.03)',
  borderRadius: theme.spacing(1.5),
}));

const SliderContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.03)',
  borderRadius: theme.spacing(1.5),
  marginTop: theme.spacing(1)
}));

const frameStyles = [
  { value: 'none', label: 'Yok', icon: <BorderClearIcon /> },
  { value: 'simple', label: 'Basit Çizgi', icon: <BorderStyleIcon /> },
  { value: 'double', label: 'Çift Çizgi', icon: <BorderAllIcon /> },
  { value: 'shadow', label: 'Gölgeli', icon: <BorderOuterIcon /> },
];

const predefinedColors = [
  '#000000', '#FFFFFF', '#FF0000', '#4CAF50', 
  '#2196F3', '#FFC107', '#9C27B0', '#795548', 
  '#607D8B', '#E91E63'
];

const FrameToolOptions = ({ settings, onChange, onApply, currentImage }) => {
  const [previewStyle, setPreviewStyle] = useState({});
  
  // Update preview when settings change
  useEffect(() => {
    updatePreview();
  }, [settings, currentImage]);
  
  const updatePreview = () => {
    let borderStyle = 'none';
    let boxShadow = 'none';
    
    if (settings.style === 'simple') {
      borderStyle = `${settings.width}px solid ${settings.color}`;
    } else if (settings.style === 'double') {
      borderStyle = `${Math.max(1, settings.width / 3)}px double ${settings.color}`;
    } else if (settings.style === 'shadow') {
      borderStyle = `${Math.max(1, settings.width / 2)}px solid ${settings.color}`;
      boxShadow = `0 0 ${settings.width}px ${settings.color}`;
    }
    
    const newStyle = {
      border: borderStyle,
      boxShadow: boxShadow,
      width: '80%',
      height: '80%',
      backgroundImage: currentImage ? `url(${currentImage})` : 'none',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      transition: 'all 0.3s ease'
    };
    
    setPreviewStyle(newStyle);
  };

  const handleStyleChange = (event) => {
    onChange({ ...settings, style: event.target.value });
  };

  const handleColorChange = (event) => {
    onChange({ ...settings, color: event.target.value });
  };
  
  const handleColorSelect = (color) => {
    onChange({ ...settings, color });
  };
  
  const handleWidthChange = (event, newValue) => {
    onChange({ ...settings, width: newValue });
  };
  
  const handleReset = () => {
    onChange({
      style: 'simple', 
      color: '#000000', 
      width: 10
    });
  };

  return (
    <OptionsPaper elevation={4}>
      <Box sx={{ position: 'relative' }}>
        <CloseButton onClick={() => onApply(false)}>
          <CloseIcon fontSize="small" />
        </CloseButton>
        
        <Stack spacing={2.5}>
          <Box>
            <OptionTitle>
              <BorderStyleIcon /> Çerçeve Seçenekleri
            </OptionTitle>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Görselinize farklı stiller ve renklerle çerçeve ekleyin
            </Typography>
          </Box>
          
          <PreviewBox>
            <Box sx={previewStyle}>
              {!currentImage && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  Çerçeve önizlemesi
                </Typography>
              )}
            </Box>
          </PreviewBox>
          
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel id="frame-style-label">Çerçeve Stili</InputLabel>
            <Select
              labelId="frame-style-label"
              value={settings.style}
              label="Çerçeve Stili"
              onChange={handleStyleChange}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: '8px'
                }
              }}
            >
              {frameStyles.map((style) => (
                <MenuItem key={style.value} value={style.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {style.icon}
                    <Typography sx={{ ml: 1 }}>{style.label}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {settings.style !== 'none' && (
            <>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PaletteIcon fontSize="small" />
                  <Typography variant="body2" sx={{ ml: 1, fontWeight: 500 }}>
                    Renk Seçimi
                  </Typography>
                </Box>
                
                <ColorGrid container spacing={1}>
                  {predefinedColors.map((color) => (
                    <Grid item key={color}>
                      <Tooltip title={color}>
                        <ColorButton 
                          color={color} 
                          onClick={() => handleColorSelect(color)}
                          sx={{
                            boxShadow: settings.color === color ? '0 0 0 3px #1976d2' : 'none',
                          }}
                        />
                      </Tooltip>
                    </Grid>
                  ))}
                  <Grid item>
                    <Tooltip title="Özel Renk">
                      <Box sx={{ position: 'relative', width: 36, height: 36 }}>
                        <input
                          type="color"
                          value={settings.color}
                          onChange={handleColorChange}
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            opacity: 0,
                            position: 'absolute',
                            cursor: 'pointer'
                          }}
                        />
                        <ColorButton 
                          color={settings.color} 
                          sx={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            pointerEvents: 'none'
                          }}
                        />
                      </Box>
                    </Tooltip>
                  </Grid>
                </ColorGrid>
              </Box>
              
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, display: 'flex', alignItems: 'center' }}>
                  <BorderOuterIcon fontSize="small" sx={{ mr: 1 }} />
                  Kalınlık: {settings.width}px
                </Typography>
                <SliderContainer>
                  <Slider
                    value={settings.width}
                    onChange={handleWidthChange}
                    min={1}
                    max={50}
                    step={1}
                    marks={[
                      { value: 1, label: '1px' },
                      { value: 25, label: '25px' },
                      { value: 50, label: '50px' },
                    ]}
                    sx={{
                      '& .MuiSlider-thumb': {
                        boxShadow: '0 0 0 8px rgba(25, 118, 210, 0.1)'
                      }
                    }}
                  />
                </SliderContainer>
              </Box>
            </>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, gap: 2 }}>
            <Button 
              variant="outlined"
              onClick={handleReset}
              startIcon={<RestartAltIcon />}
              fullWidth
            >
              Sıfırla
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => onApply(true)}
              fullWidth
            >
              Uygula
            </Button>
          </Box>
        </Stack>
      </Box>
    </OptionsPaper>
  );
};

export default FrameToolOptions;
